import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../firebase/config';
import { Globe, CheckCircle } from 'lucide-react';

// 해외 기공소 가입 (영문) — 기획서 §1: 오픈 가입 + 덴클 승인 심사(pending_review)
const COUNTRIES = ['United States', 'Canada', 'United Kingdom', 'Germany', 'France', 'Australia', 'Japan', 'Vietnam', 'Thailand', 'Philippines', 'Indonesia', 'UAE', 'Saudi Arabia', 'Other'];
const CURRENCIES = ['USD', 'EUR', 'GBP', 'AUD', 'JPY', 'KRW'];

function JoinGlobal() {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        email: '', password: '', passwordConfirm: '',
        labName: '', country: '', currency: 'USD', contactName: '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);
    // 필수 동의 (약관 v6.2 · 개인정보 v3.2.3 — Register.js와 동일 버전, 2026-10-04 정합 4) — 국내 Register consents 구조와 동일 필드
    const [agreedTerms, setAgreedTerms] = useState(false);
    const [agreedPrivacy, setAgreedPrivacy] = useState(false);

    const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

    const submit = async () => {
        setError('');
        if (!form.email || !form.password || !form.labName || !form.country || !form.contactName) {
            setError('Please fill in all required fields.');
            return;
        }
        if (form.password.length < 6) { setError('Password must be at least 6 characters.'); return; }
        if (form.password !== form.passwordConfirm) { setError('Passwords do not match.'); return; }
        if (!agreedTerms || !agreedPrivacy) {
            setError('You must agree to the Terms of Service and Privacy Policy.');
            return;
        }
        setLoading(true);
        try {
            const consentTimestamp = new Date().toISOString();
            const cred = await createUserWithEmailAndPassword(auth, form.email.trim(), form.password);
            await setDoc(doc(db, 'users', cred.user.uid), {
                email: form.email.trim(),
                name: form.contactName.trim(),
                businessName: form.labName.trim(),
                representativeName: form.contactName.trim(),
                userType: 'business',
                businessType: 'lab',
                labKind: 'overseas',
                country: form.country,
                currency: form.currency,
                globalStatus: 'pending_review',   // 덴클 관리자 승인 후 'active'
                approvalStatus: 'pending',        // 🔐 가입 승인제 통일 필드 (2026-08-25) — AdminGlobalLabs 심사 시 globalStatus와 함께 갱신
                createdAt: serverTimestamp(),
                // 약관 동의 기록 (법적 증빙용 — 국내 Register consents와 동일 구조)
                consents: {
                    terms: { agreed: true, version: 'v6.2', agreedAt: consentTimestamp },
                    privacy: { agreed: true, version: 'v3.2.3', agreedAt: consentTimestamp },
                },
            });
            setDone(true);
            // 가입 성공 = 로그인 상태 전환 → 앱이 리마운트되므로 상태 화면으로 즉시 이동
            setTimeout(() => { window.location.href = '/global/status'; }, 1200);
        } catch (e) {
            if (e.code === 'auth/email-already-in-use') setError('This email is already registered.');
            else if (e.code === 'auth/invalid-email') setError('Invalid email address.');
            else setError('Sign-up failed. Please try again.');
            console.error('global signup 실패:', e);
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <div style={S.page}>
                <div style={S.card}>
                    <CheckCircle size={44} color="#0E8A74" />
                    <h1 style={S.title}>Application received</h1>
                    <p style={S.sub}>
                        Thank you, <b>{form.labName}</b>. Our team will review your application and
                        activate your account. Approval usually takes 1–2 business days.
                        Please log in to check your status.
                    </p>
                    <button style={S.primary} onClick={() => navigate('/login')}>Go to sign in</button>
                </div>
            </div>
        );
    }

    return (
        <div style={S.page}>
            <div style={S.card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Globe size={26} color="#4F52C7" />
                    <div>
                        <h1 style={S.title}>Join DentCluster Global</h1>
                        <p style={S.sub}>Outsource dental work to verified Korean labs — files, orders and invoices in one place. No email attachments.</p>
                    </div>
                </div>

                <label style={S.label}>Email *</label>
                <input style={S.input} type="email" value={form.email} onChange={set('email')} placeholder="you@yourlab.com" />

                <div style={{ display: 'flex', gap: 10 }}>
                    <div style={{ flex: 1 }}>
                        <label style={S.label}>Password *</label>
                        <input style={S.input} type="password" value={form.password} onChange={set('password')} placeholder="6+ characters" />
                    </div>
                    <div style={{ flex: 1 }}>
                        <label style={S.label}>Confirm password *</label>
                        <input style={S.input} type="password" value={form.passwordConfirm} onChange={set('passwordConfirm')} />
                    </div>
                </div>

                <label style={S.label}>Lab / company name *</label>
                <input style={S.input} value={form.labName} onChange={set('labName')} placeholder="e.g. Smile Dental Lab" />

                <div style={{ display: 'flex', gap: 10 }}>
                    <div style={{ flex: 1 }}>
                        <label style={S.label}>Country *</label>
                        <select style={S.input} value={form.country} onChange={set('country')}>
                            <option value="">Select…</option>
                            {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div style={{ flex: 1 }}>
                        <label style={S.label}>Billing currency</label>
                        <select style={S.input} value={form.currency} onChange={set('currency')}>
                            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                </div>

                <label style={S.label}>Contact person *</label>
                <input style={S.input} value={form.contactName} onChange={set('contactName')} placeholder="Full name" />

                {/* 필수 동의 — Terms(디자인외주 4.5% 포함)·Privacy. 영문 약관 페이지 부재로 국문 원문 링크 연결 */}
                <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <label style={S.consentRow}>
                        <input type="checkbox" checked={agreedTerms} onChange={(e) => setAgreedTerms(e.target.checked)} style={{ marginTop: 2 }} />
                        <span>
                            I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" style={S.link}>Terms of Service</a>. *
                            <span style={S.consentNote}> The official text is currently available in Korean.</span>
                        </span>
                    </label>
                    <label style={S.consentRow}>
                        <input type="checkbox" checked={agreedPrivacy} onChange={(e) => setAgreedPrivacy(e.target.checked)} style={{ marginTop: 2 }} />
                        <span>
                            I agree to the <a href="/privacy" target="_blank" rel="noopener noreferrer" style={S.link}>Privacy Policy</a>. *
                        </span>
                    </label>
                </div>

                {error && <div style={S.error}>{error}</div>}
                <button style={S.primary} onClick={submit} disabled={loading}>
                    {loading ? 'Submitting…' : 'Apply for an account'}
                </button>
                <p style={{ ...S.sub, fontSize: 12, marginTop: 12 }}>
                    Applications are reviewed by the DentCluster team before activation.
                    Already have an account? <span style={S.link} onClick={() => navigate('/login')}>Sign in</span>
                </p>
            </div>
        </div>
    );
}

const S = {
    page: { minHeight: '100vh', background: '#F6F7FB', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
    card: { width: '100%', maxWidth: 460, background: '#fff', border: '1px solid #E4E6EE', borderRadius: 14, padding: '28px 26px', boxShadow: '0 8px 24px rgba(30,34,48,.06)' },
    title: { fontSize: 20, fontWeight: 800, color: '#1E2230', margin: 0 },
    sub: { fontSize: 13, color: '#5A6072', margin: '6px 0 14px', lineHeight: 1.6 },
    label: { display: 'block', fontSize: 12, fontWeight: 700, color: '#5A6072', margin: '10px 0 4px' },
    input: { width: '100%', padding: '10px 12px', border: '1px solid #E4E6EE', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', background: '#fff' },
    error: { color: '#DC2626', fontSize: 13, marginTop: 10 },
    primary: { width: '100%', marginTop: 16, padding: '12px', background: '#4F52C7', color: '#fff', border: 'none', borderRadius: 9, fontSize: 15, fontWeight: 700, cursor: 'pointer' },
    link: { color: '#4F52C7', fontWeight: 700, cursor: 'pointer' },
    consentRow: { display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#1E2230', lineHeight: 1.5, cursor: 'pointer' },
    consentNote: { color: '#8A90A3', fontSize: 12 },
};

export default JoinGlobal;
